import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  Mail, FileText, Monitor, Save, RotateCcw, Eye, Edit, 
  AlertCircle, CheckCircle, Code, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Template type icons
const typeIcons = {
  email: Mail,
  screen: Monitor,
  pdf: FileText,
};

// Template type labels
const typeLabels = {
  email: 'Email Template',
  screen: 'Success Screen',
  pdf: 'PDF Template',
};

const TemplateManager = ({ committeeId = null, isAdmin = false }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, template: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, content: '', subject: '' });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('email');
  
  // Editor state
  const [editContent, setEditContent] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'code'

  const fetchTemplates = useCallback(async () => {
    try {
      const params = committeeId ? `?committee_id=${committeeId}` : '';
      const response = await axios.get(`${API}/templates${params}`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [committeeId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openEditDialog = (template) => {
    setEditContent(template.content);
    setEditSubject(template.subject || '');
    setEditorMode('visual');
    setEditDialog({ open: true, template });
  };

  const handleSave = async () => {
    if (!editDialog.template) return;
    
    setSaving(true);
    try {
      const params = committeeId ? `?committee_id=${committeeId}` : '';
      await axios.put(`${API}/templates/${editDialog.template.template_key}${params}`, {
        content: editContent,
        subject: editSubject || null
      });
      
      toast.success('Template saved successfully');
      setEditDialog({ open: false, template: null });
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (templateKey) => {
    if (!window.confirm('Are you sure you want to restore this template to its default? Your customizations will be lost.')) {
      return;
    }
    
    try {
      const params = committeeId ? `?committee_id=${committeeId}` : '';
      await axios.post(`${API}/templates/${templateKey}/restore${params}`);
      
      toast.success('Template restored to default');
      fetchTemplates();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('Template is already using default');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to restore template');
      }
    }
  };

  const handlePreview = async () => {
    if (!editDialog.template) return;
    
    try {
      const params = committeeId ? `?committee_id=${committeeId}` : '';
      const response = await axios.post(`${API}/templates/${editDialog.template.template_key}/preview${params}`, {
        content: editContent,
        subject: editSubject || null
      });
      
      setPreviewDialog({
        open: true,
        content: response.data.rendered_content,
        subject: response.data.rendered_subject,
        variables: response.data.sample_variables
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const filteredTemplates = templates.filter(t => t.template_type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {committeeId ? 'Committee Templates' : 'Global Templates'}
          </h2>
          <p className="text-sm text-slate-500">
            {committeeId 
              ? 'Customize templates for your committee. These override global templates.'
              : 'Manage global email templates and success screens.'}
          </p>
        </div>
      </div>

      {/* Template Type Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="screen" className="flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Screens
          </TabsTrigger>
          <TabsTrigger value="pdf" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            PDF
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <div className="grid gap-4">
            {filteredTemplates.map((template) => {
              const TypeIcon = typeIcons[template.template_type] || Mail;
              return (
                <div
                  key={template.template_key}
                  className={`bg-white rounded-lg border p-4 ${
                    template.is_customized ? 'border-greek-gold' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        template.is_customized ? 'bg-greek-gold/10' : 'bg-slate-100'
                      }`}>
                        <TypeIcon className={`w-5 h-5 ${
                          template.is_customized ? 'text-greek-gold' : 'text-slate-500'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{template.name}</h3>
                          {template.is_customized && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-greek-gold/10 text-greek-gold rounded-full">
                              Customized
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                        {template.subject && (
                          <p className="text-xs text-slate-400 mt-2">
                            <strong>Subject:</strong> {template.subject}
                          </p>
                        )}
                        {template.variables && template.variables.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {template.variables.map((v) => (
                              <span
                                key={v.name}
                                className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded"
                                title={v.description}
                              >
                                {`{{${v.name}}}`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {template.is_customized && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(template.template_key)}
                          className="text-slate-600"
                          title="Restore to default"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => openEditDialog(template)}
                        className="bg-greek-blue hover:bg-greek-blue-dark"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredTemplates.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No {typeLabels[activeTab]?.toLowerCase() || 'templates'} found
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog.open} 
        onOpenChange={(open) => !open && setEditDialog({ open: false, template: null })}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit: {editDialog.template?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Subject line for emails */}
            {editDialog.template?.template_type === 'email' && (
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="rounded-sm"
                />
                <p className="text-xs text-slate-500">
                  Available variables: {editDialog.template?.variables?.map(v => `{{${v.name}}}`).join(', ')}
                </p>
              </div>
            )}
            
            {/* Editor mode toggle */}
            <div className="flex items-center justify-between">
              <Label>Content</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant={editorMode === 'visual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditorMode('visual')}
                  className="h-8"
                >
                  <Palette className="w-4 h-4 mr-1" />
                  Visual
                </Button>
                <Button
                  variant={editorMode === 'code' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditorMode('code')}
                  className="h-8"
                >
                  <Code className="w-4 h-4 mr-1" />
                  HTML
                </Button>
              </div>
            </div>
            
            {/* Editor */}
            {editorMode === 'visual' ? (
              <div className="border rounded-sm">
                <RichTextEditor
                  content={editContent}
                  onChange={setEditContent}
                  placeholder="Enter template content..."
                />
              </div>
            ) : (
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-96 p-4 font-mono text-sm border rounded-sm resize-none focus:outline-none focus:ring-2 focus:ring-greek-blue"
                placeholder="Enter HTML content..."
              />
            )}
            
            {/* Variables reference */}
            {editDialog.template?.variables && editDialog.template.variables.length > 0 && (
              <div className="bg-slate-50 p-4 rounded-sm">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Available Variables</h4>
                <div className="grid grid-cols-2 gap-2">
                  {editDialog.template.variables.map((v) => (
                    <div key={v.name} className="text-sm">
                      <code className="bg-slate-200 px-1 rounded">{`{{${v.name}}}`}</code>
                      <span className="text-slate-500 ml-2">{v.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex items-center justify-between border-t pt-4">
            <Button
              variant="outline"
              onClick={handlePreview}
              className="rounded-sm"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setEditDialog({ open: false, template: null })}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-greek-blue hover:bg-greek-blue-dark rounded-sm"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Template'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog.open} 
        onOpenChange={(open) => !open && setPreviewDialog({ open: false, content: '', subject: '' })}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Template Preview
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {previewDialog.subject && (
              <div className="mb-4 p-3 bg-slate-100 rounded-sm">
                <span className="text-sm font-medium text-slate-700">Subject: </span>
                <span className="text-sm text-slate-900">{previewDialog.subject}</span>
              </div>
            )}
            
            {previewDialog.variables && (
              <div className="mb-4 p-3 bg-blue-50 rounded-sm">
                <p className="text-xs text-blue-700 font-medium mb-1">Sample values used in preview:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(previewDialog.variables).map(([key, value]) => (
                    <span key={key} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      {key}: {value}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="border rounded-sm bg-white">
              <iframe
                srcDoc={previewDialog.content}
                className="w-full h-[500px] border-0"
                title="Template Preview"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setPreviewDialog({ open: false, content: '', subject: '' })}
              className="rounded-sm"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateManager;
